import { useCallback, useEffect, useState } from 'react'
import { chores, family, kids, store, type Assignment, type Family, type Member, type Template } from '../lib/chores'

type Tab = 'today' | 'kids' | 'chores' | 'store'

/**
 * The parent's side.
 *
 * "Today" lands first and answers one question — what is waiting for me. The
 * rule for a row here is the factory's: actionable AND finite. A count that only
 * ever grows is a statistic and belongs further in.
 */
export default function ParentHome({ onChange }: { onChange: () => Promise<void> }) {
  const [tab, setTab] = useState<Tab>('today')
  const [house, setHouse] = useState<{ family: Family | null; members: Member[] } | null>(null)
  const [waiting, setWaiting] = useState<Assignment[]>([])
  const [templates, setTemplates] = useState<Template[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    const [h, submitted, t] = await Promise.all([
      family.show(),
      chores.assignments('?status=submitted'),
      chores.templates(),
    ])
    setHouse(h)
    setWaiting(submitted.assignments)
    setTemplates(t.templates)
  }, [])

  useEffect(() => { void load() }, [load])

  async function run(fn: () => Promise<unknown>) {
    setBusy(true); setError(null)
    try { await fn(); await load(); await onChange() }
    catch (e) { setError((e as Error).message) }
    finally { setBusy(false) }
  }

  const children = (house?.members ?? []).filter((m) => m.isChild)

  return (
    <section className="card">
      <nav className="tabs">
        <button onClick={() => setTab('today')} aria-current={tab === 'today'}>
          Today{waiting.length ? ` (${waiting.length})` : ''}
        </button>
        <button onClick={() => setTab('kids')} aria-current={tab === 'kids'}>Kids</button>
        <button onClick={() => setTab('chores')} aria-current={tab === 'chores'}>Chores</button>
        <button onClick={() => setTab('store')} aria-current={tab === 'store'}>Store</button>
      </nav>

      {error && <p className="error" role="alert">{error}</p>}

      {tab === 'today' && (
        <>
          <h2>Waiting for you</h2>
          <ul className="list">
            {waiting.length === 0 && <li className="muted">Nothing to approve. </li>}
            {waiting.map((a) => (
              <li key={a.id}>
                <div>
                  <strong>{a.name}</strong>
                  <div className="muted small">{a.child_name} · {a.points} points · due {a.due_on}</div>
                </div>
                <div className="row">
                  <button disabled={busy} onClick={() => run(() => chores.review(a.id, true))}>Approve</button>
                  <button disabled={busy} onClick={() => run(() => chores.review(a.id, false))}>Not yet</button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {tab === 'kids' && <KidsTab members={children} onRun={run} busy={busy} house={house} />}
      {tab === 'chores' && <ChoresTab templates={templates} children={children} onRun={run} busy={busy} />}
      {tab === 'store' && <StoreTab onRun={run} busy={busy} />}
    </section>
  )
}

function KidsTab({ members, onRun, busy, house }: {
  members: Member[]; busy: boolean
  onRun: (fn: () => Promise<unknown>) => Promise<void>
  house: { family: Family | null } | null
}) {
  const [handle, setHandle] = useState('')
  const [name, setName] = useState('')
  const [pin, setPin] = useState('')
  const [locked, setLocked] = useState<{ id: string; handle: string; locked: boolean }[]>([])

  useEffect(() => { void kids.list().then((d) => setLocked(d.delegates)).catch(() => {}) }, [members.length])

  return (
    <>
      <ul className="list">
        {members.map((m) => {
          const state = locked.find((l) => l.id === m.id)
          return (
            <li key={m.id}>
              <div>
                <strong>{m.display_name}</strong> <span className="muted">@{m.handle}</span>
                <div className="muted small">{m.points} points · {m.coins} coins</div>
                {state?.locked && <div className="warn">Locked out — too many wrong PINs.</div>}
              </div>
              <div className="row">
                {state?.locked && <button disabled={busy} onClick={() => onRun(() => kids.unlock(m.id))}>Unlock</button>}
                <button disabled={busy} onClick={() => onRun(() => kids.giveCoins(m.id, 1))}>+1 coin</button>
              </div>
            </li>
          )
        })}
      </ul>

      <h3>Add a kid</h3>
      {/*
        A child gets a handle and a PIN, not a passkey — a seven-year-old on the
        family tablet cannot manage a credential. The PIN is honestly weak; five
        wrong tries locks the account and only you can open it.
      */}
      <label>Name<input value={name} onChange={(e) => setName(e.target.value)} /></label>
      <label>Username<input value={handle} onChange={(e) => setHandle(e.target.value.toLowerCase())} autoCapitalize="none" /></label>
      <label>PIN (4–12 digits)
        <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} inputMode="numeric" maxLength={12} />
      </label>
      <button className="primary" disabled={busy || !handle || pin.length < 4}
        onClick={() => onRun(async () => { await kids.create(handle, name || handle, pin); setHandle(''); setName(''); setPin('') })}>
        Add them
      </button>

      {house?.family?.invite_code && (
        <p className="muted small">
          Another parent can join with code <strong>{house.family.invite_code}</strong>.
        </p>
      )}
    </>
  )
}

function ChoresTab({ templates, children, onRun, busy }: {
  templates: Template[]; children: Member[]; busy: boolean
  onRun: (fn: () => Promise<unknown>) => Promise<void>
}) {
  const [name, setName] = useState('')
  const [points, setPoints] = useState('10')

  return (
    <>
      <ul className="list">
        {templates.length === 0 && <li className="muted">No chores yet.</li>}
        {templates.map((t) => (
          <li key={t.id}>
            <div><strong>{t.name}</strong><div className="muted small">{t.points} points</div></div>
            <div className="row">
              {children.map((c) => (
                <button key={c.id} disabled={busy} onClick={() => onRun(() => chores.assign(t.id, c.id))}>
                  → {c.display_name}
                </button>
              ))}
            </div>
          </li>
        ))}
      </ul>

      <h3>New chore</h3>
      <label>What is it?<input value={name} onChange={(e) => setName(e.target.value)} /></label>
      <label>Worth<input value={points} onChange={(e) => setPoints(e.target.value.replace(/\D/g, ''))} inputMode="numeric" /></label>
      <button className="primary" disabled={busy || !name || !points}
        onClick={() => onRun(async () => { await chores.createTemplate({ name, points: Number(points) }); setName('') })}>
        Add it
      </button>
    </>
  )
}

function StoreTab({ onRun, busy }: { onRun: (fn: () => Promise<unknown>) => Promise<void>; busy: boolean }) {
  const [items, setItems] = useState<{ id: string; name: string; points_cost: number; quantity: number | null }[]>([])
  const [name, setName] = useState('')
  const [cost, setCost] = useState('30')

  useEffect(() => { void store.items().then((d) => setItems(d.items)).catch(() => {}) }, [busy])

  return (
    <>
      <ul className="list">
        {items.length === 0 && <li className="muted">Nothing in the store yet.</li>}
        {items.map((i) => (
          <li key={i.id}>
            <div><strong>{i.name}</strong>
              <div className="muted small">{i.points_cost} points{i.quantity !== null ? ` · ${i.quantity} left` : ''}</div>
            </div>
          </li>
        ))}
      </ul>

      <h3>New prize</h3>
      <label>What is it?<input value={name} onChange={(e) => setName(e.target.value)} /></label>
      <label>Costs<input value={cost} onChange={(e) => setCost(e.target.value.replace(/\D/g, ''))} inputMode="numeric" /></label>
      <button className="primary" disabled={busy || !name || !cost}
        onClick={() => onRun(async () => { await store.createItem({ name, pointsCost: Number(cost) }); setName('') })}>
        Add it
      </button>
    </>
  )
}
